import React, { useState, useEffect, useRef } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Stack,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, AccountCircle, Lock, OpenInNew } from '@mui/icons-material';
import axios from 'axios';
import { ENDPOINTS } from '../../config';
import firebase from 'firebase/app';
import 'firebase/auth';

const ClaimAccount = () => {
  const { venueId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const { sessionId, venue } = location.state || {};

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const errorRef = useRef(null);

  useEffect(() => {
    document.title = 'Create Your Account | Piatto';
  }, []);

  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.focus();
    }
  }, [error]);

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const validateForm = () => {
    if (!formData.name || !formData.email || !formData.password) {
      setError('Please fill in all required fields');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }

    return true;
  };

  const handleCreateAccount = async () => {
    if (!validateForm()) {
      return;
    }

    if (!sessionId) {
      setError('Invalid session. Please restart the claim process.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Create Firebase Auth account
      const userCredential = await firebase
        .auth()
        .createUserWithEmailAndPassword(formData.email, formData.password);

      const user = userCredential.user;

      // Update user profile
      await user.updateProfile({
        displayName: formData.name,
      });

      // Get Firebase ID token
      const idToken = await user.getIdToken();

      // Complete the claim with the backend
      const response = await axios.post(
        ENDPOINTS.completeClaim,
        {
          sessionId,
          userProfile: {
            name: formData.name,
            role: 'Owner',
          },
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      // Navigate to success page
      history.push(`/claim-venue/${venueId}/success`, {
        venue: response.data.venue,
        dashboardUrl: response.data.dashboardUrl,
      });
    } catch (err) {
      console.error('Error creating account:', err);

      // Handle Firebase Auth errors
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already in use. Please sign in instead.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else {
        setError(
          err.response?.data?.error ||
          'Failed to create account. Please try again.'
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <AccountCircle sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Create Your Account
        </Typography>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          {venue?.name || 'Your Venue'}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          You're creating your <strong>Piatto venue management account</strong>. The email
          and password you choose below will be your sign-in credentials for the Piatto
          venue dashboard at{' '}
          <a href="https://piatto.com.au/SignIn" target="_blank" rel="noopener noreferrer"
            style={{ color: '#B91C1C' }}>
            piatto.com.au/SignIn
          </a>.
        </Typography>
      </Box>

      <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} role="alert" ref={errorRef} tabIndex={-1}>
              {error}
            </Alert>
          )}

          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Full Name"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="John Smith"
              required
              inputProps={{ 'aria-required': 'true' }}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              placeholder="john@yourvenue.com"
              required
              inputProps={{ 'aria-required': 'true' }}
              disabled={loading}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange('password')}
              required
              inputProps={{ 'aria-required': 'true' }}
              disabled={loading}
              helperText="Must be at least 8 characters"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <VisibilityOff aria-hidden="true" /> : <Visibility aria-hidden="true" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Alert severity="warning" icon={<Lock fontSize="inherit" aria-hidden="true" />} sx={{ textAlign: 'left' }}>
              <strong>Save your credentials securely.</strong> Store your email address and
              password in a safe place (e.g. a password manager) before continuing. You will
              need them every time you sign in to manage your venue at{' '}
              <a href="https://piatto.com.au/SignIn" target="_blank" rel="noopener noreferrer"
                style={{ color: 'inherit', fontWeight: 600 }}>
                piatto.com.au/SignIn <OpenInNew sx={{ fontSize: 12, verticalAlign: 'middle' }} />
              </a>.
              {' '}If you forget your password, use the "Reset Password" link on the sign-in page.
            </Alert>

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={handleCreateAccount}
              disabled={loading}
              sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600, mt: 2 }}
            >
              {loading ? (
                <>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  Creating Account...
                </>
              ) : (
                'Create Account & Claim Venue'
              )}
            </Button>

            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
              By creating an account, you agree to Piatto's{' '}
              <a
                href="https://piatto.com.au/Piatto_Terms_and_Conditions_for_Venues.pdf"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}
              >
                Terms and Conditions
              </a>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ClaimAccount;
